import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  ElementTransformer,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  Transformer,
} from '@lexical/markdown';
import {
  $createTableCellNode,
  $createTableNode,
  $createTableRowNode,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import {
  $copyNode,
  $createTextNode,
  $isElementNode,
  $isParagraphNode,
  $isTextNode,
  ElementNode,
  LexicalNode,
} from 'lexical';

/**
 * GFM-style markdown table support. `@lexical/markdown` ships no table
 * transformer, so pipe-tables (`| a | b |`) would otherwise survive as literal
 * text. This is the canonical Lexical-playground table transformer, adapted to
 * our codebase, so tables round-trip in every surface that uses MD_TRANSFORMERS
 * (the markdown editor, the policy drawer, the PDF export, the document preview).
 *
 * On import it recognizes a header only when the next row is a valid delimiter,
 * so `| note |` stays text while both `| a | b |` and `a | b` table rows work.
 * Inline Markdown inside each cell is parsed and preserved on export.
 */

const TABLE_ROW_REG_EXP = /^.*\|.*\s?$/;
const TABLE_CELL_DIVIDER_REG_EXP = /^:?-{3,}:?$/;
const INLINE_CELL_TRANSFORMERS: Transformer[] = [
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
];

export const TABLE: ElementTransformer = {
  type: 'element',
  dependencies: [TableNode, TableRowNode, TableCellNode],
  export: (node: LexicalNode) => {
    if (!$isTableNode(node)) {
      return null;
    }

    const output: string[] = [];

    for (const row of node.getChildren()) {
      const rowOutput: string[] = [];
      if (!$isTableRowNode(row)) {
        continue;
      }

      let isHeaderRow = false;
      for (const cell of row.getChildren()) {
        if ($isTableCellNode(cell)) {
          rowOutput.push(
            $convertToMarkdownString(INLINE_CELL_TRANSFORMERS, cell).replace(/\n/g, '\\n').trim(),
          );
          if (cell.getHeaderStyles() === TableCellHeaderStates.ROW) {
            isHeaderRow = true;
          }
        }
      }

      output.push(`| ${rowOutput.join(' | ')} |`);
      if (isHeaderRow) {
        output.push(`| ${rowOutput.map(() => '---').join(' | ')} |`);
      }
    }

    return output.join('\n');
  },
  regExp: TABLE_ROW_REG_EXP,
  replace: (parentNode, _children, match, isImport) => {
    const line = match[0] ?? '';
    const cells = splitTableRow(line);
    if (cells == null) {
      preserveAsText(parentNode, line, isImport);
      return;
    }

    const previousSibling = parentNode.getPreviousSibling();
    if (isDividerRow(cells)) {
      if (!$isParagraphNode(previousSibling)) {
        preserveAsText(parentNode, line, isImport);
        return;
      }

      const headerMarkdown = $convertToMarkdownString(
        INLINE_CELL_TRANSFORMERS,
        $copyIntoTableCell(previousSibling),
      );
      const headerCells = splitTableRow(headerMarkdown);
      if (headerCells == null || headerCells.length !== cells.length) {
        preserveAsText(parentNode, line, isImport);
        return;
      }

      const table = $createTable([headerCells], TableCellHeaderStates.ROW);
      previousSibling.replace(table);
      parentNode.remove();
      if (!isImport) table.selectEnd();
      return;
    }

    if ($isTableNode(previousSibling) && getTableColumnsSize(previousSibling) === cells.length) {
      previousSibling.append($createTableRow(cells));
      parentNode.remove();
      if (!isImport) previousSibling.selectEnd();
      return;
    }

    preserveAsText(parentNode, line, isImport);
  },
};

function getTableColumnsSize(table: TableNode): number {
  const row = table.getFirstChild();
  return $isTableRowNode(row) ? row.getChildrenSize() : 0;
}

const $createTable = (
  rows: string[][],
  headerState = TableCellHeaderStates.NO_STATUS,
): TableNode => {
  const table = $createTableNode();
  table.append(...rows.map((cells) => $createTableRow(cells, headerState)));
  return table;
};

const $createTableRow = (
  cells: string[],
  headerState = TableCellHeaderStates.NO_STATUS,
): TableRowNode => {
  const row = $createTableRowNode();
  row.append(...cells.map((text) => $createTableCell(text, headerState)));
  return row;
};

const $createTableCell = (
  textContent: string,
  headerState = TableCellHeaderStates.NO_STATUS,
): TableCellNode => {
  const cell = $createTableCellNode(headerState);
  $convertFromMarkdownString(
    textContent.replace(/\\n/g, '\n').trim(),
    INLINE_CELL_TRANSFORMERS,
    cell,
    // `First\nSecond` is decoded above; `true` keeps two cell paragraphs instead of merging them.
    true,
  );
  return cell;
};

const splitTableRow = (textContent: string): string[] | null => {
  const value = textContent.trim();
  const cells: string[] = [];
  let cell = '';
  let hasSeparator = false;

  for (let index = 0; index < value.length; index++) {
    const character = value[index];
    if (character === '|' && !isEscaped(value, index)) {
      cells.push(cell);
      cell = '';
      hasSeparator = true;
    } else {
      cell += character;
    }
  }
  cells.push(cell);

  if (!hasSeparator) {
    return null;
  }

  if (value.startsWith('|')) cells.shift();
  if (value.endsWith('|') && !isEscaped(value, value.length - 1)) cells.pop();

  return cells.map((value) => value.trim());
};

const isDividerRow = (cells: string[]) =>
  cells.length > 0 && cells.every((cell) => TABLE_CELL_DIVIDER_REG_EXP.test(cell));

const isEscaped = (value: string, index: number) => {
  let backslashes = 0;
  while (index > 0 && value[--index] === '\\') backslashes++;
  return backslashes % 2 === 1;
};

const preserveAsText = (parentNode: LexicalNode, value: string, isImport: boolean) => {
  if (!$isParagraphNode(parentNode)) return;
  const textNode = parentNode.getFirstChild();
  if ($isTextNode(textNode)) {
    textNode.setTextContent(value.trim());
  } else {
    parentNode.clear();
    parentNode.append($createTextNode(value.trim()));
  }
  if (!isImport) parentNode.selectEnd();
};

// The header has already become inline Lexical nodes before its delimiter arrives;
// clone it so serialization does not move the live nodes out of the editor.
const $copyIntoTableCell = (node: ElementNode) => {
  const cell = $createTableCellNode(TableCellHeaderStates.NO_STATUS);
  cell.append($copyNodeTree(node));
  return cell;
};

const $copyNodeTree = (node: LexicalNode): LexicalNode => {
  const copy = $copyNode(node);
  if ($isTextNode(node) && $isTextNode(copy)) {
    copy
      .setFormat(node.getFormat())
      .setDetail(node.getDetail())
      .setMode(node.getMode())
      .setStyle(node.getStyle());
  }
  if ($isElementNode(node) && $isElementNode(copy)) {
    copy.append(...node.getChildren().map($copyNodeTree));
  }
  return copy;
};
